import React, { useEffect, useState } from 'react';

import ComboBox from '../../../../../components/ComboBox';
import CollapsableSection from '../../../../../components/CollapsibleSection';
import ButtonForm from '../../../../../components/ButtonForm';
import InputForm from '../../../../../components/InputForm';
import WidgetHeaderStyleFields from '../../../../../components/WidgetHeaderStyleFields';
import DropdownSelectableList from '../../../../../components/icons/DropdownSelectableList';
import SelectionForm from '../../../../../components/SelectionForm';

import CheckIco from '../../../../../components/icons/CheckIco';
import TrashIco from '../../../../../components/icons/TrashIco';
import AddIco from '../../../../../components/icons/AddIco';

import Spinner from '../../../../../components/Spinner';
import { useAuth } from '../../../../../providers/AuthContext';
import { useAppContext } from '../../../../../providers/AppContext';
import { useNavigate } from 'react-router-dom';
import { useWidgets } from '../../../../../providers/WidgetContext';
import { useDashboards } from '../../../../../providers/DashboardContext';
import { createWidget, getWidgetById, updateWidget } from '../../../../../services/apiWidgests';
import { getAllParameters } from '../../../../../services/apiProcessParameteres';
import { translateDatePicker } from '../../../../utils';
import { useProcesses } from '../../../../../providers/ProcessesContext';

const ColumnField = ({ header, onDelete = () => {} }) => {
  const aliasOrKey = header?.target?.alias || header?.target?.key || header?.alias || header?.key || "Sin nombre";
  const timeVarAlias = header?.timeVar?.alias || header?.timeVar?.key || header?.timeVar?.target?.alias || header?.timeVar?.target?.key;
  const key = header?.target?.key || header?.key || "";
  const whereArray = header?.where || [];

  return (
    <div className="flex justify-between items-center w-full mr-3 border rounded-md p-2 bg-gray-50 hover:bg-gray-100 transition">
      {/* Alias o Key */}
      <div className="flex flex-col basis-1/3 text-gray-800 font-medium">
        <span>{aliasOrKey}</span>
        {timeVarAlias && (
          <span className="text-xs text-blue-600 font-normal">⏱ {timeVarAlias}</span>
        )}
      </div>

      {/* Where conditions */}
      <div 
        className="basis-1/3 text-sm text-gray-500 truncate" 
        title={whereArray.map(w => `${w.field} ${w.operator === 'equal' ? '=' : w.operator === 'notEqual' ? '≠' : w.operator === 'contains' ? '~=' : w.operator === 'startsWith' ? '^=' : ''} ${w.value}`).join(", ")}
      >
        {whereArray.length > 0
          ? whereArray.map(w => `${w.field} ${w.operator === 'equal' ? '=' : w.operator === 'notEqual' ? '≠' : w.operator === 'contains' ? '~=' : w.operator === 'startsWith' ? '^=' : ''} ${w.value}`).join(", ")
          : "—"}
      </div>

      {/* Key + eliminar */}
      <div className="flex items-center gap-2 basis-1/3 justify-end">
        <span className="text-gray-400 text-xs">{key}</span>
        <button
          type="button"
          className="p-1 hover:bg-red-50 rounded-full"
          onClick={() => onDelete()}
        >
          <TrashIco fill="red" className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

function QualityForm({ onSubmit = () => {}, id }) {
  const { auth } = useAuth();
  const { addToast } = useAppContext();
  const navigate = useNavigate();
  
  const { addCard, assignSpace } = useWidgets();
  const { dashboard, selectedTab } = useDashboards();
  const { menuItems } = useProcesses();

  const [processList, setProcessList] = useState({});
  const [selectedConformProcess, setSelectedConformProcess] = useState();
  const [selectedNoConformProcess, setSelectedNoConformProcess] = useState();
  const [selectedReProcess, setSelectedReProcess] = useState();

  const [conformParameters, setConformParameters] = useState([]);
  const [noConformParameters, setNoConformParameters] = useState([]);
  const [reParameters, setReParameters] = useState([]);

  const [fetchingConformParams, setFetchingConformParams] = useState(false);
  const [fetchingNoConformParams, setFetchingNoConformParams] = useState(false);
  const [fetchingReParams, setFetchingReParams] = useState(false);

  const [dataRange, setDataRange] = useState('Asignar por dashboard');
  const [title, setTitle] = useState('');
  const [validate, setValidate] = useState(false);
  const [conf, setConf] = useState();
  const [stage, setStage] = useState("form");

  const [loading, setLoading] = useState(true);
  const [creatingWidget, setCreatingWidget] = useState(false);
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);

  const OPERATOR_OPTIONS = [
    { label: "Igual a", value: "equal" },
    { label: "Diferente de", value: "notEqual" },
    { label: "Contiene", value: "contains" }
  ];

  const graphTypes = {
    "line": "Gráfico de línea",
    "VBars": "Gráfico de barras vertical",
    "HBars": "Gráfico de barras horizontal"
  };

  const colorOptions = {
    "pastel": "Paleta pastel",
    "blue": "Paleta azul",
    "red": "Paleta roja",
    "fixed": "Color fijo"
  };

  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setConf(response.configuration);
      setTitle(response.title || "");
      
      if (response.configuration?.quality?.conformValue?.process?.id) {
        setSelectedConformProcess(response.configuration.quality.conformValue.process.id);
      }
      if (response.configuration?.quality?.noConformValue?.process?.id) {
        setSelectedNoConformProcess(response.configuration.quality.noConformValue.process.id);
      }
      if (response.configuration?.quality?.reValue?.process?.id) {
        setSelectedReProcess(response.configuration.quality.reValue.process.id);
      }
      
      setFetchingWidgetInfo(false);
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 404) {
        addToast("Widget no encontrado", 3);
      } else if (error?.response?.status === 419) {
        addToast("Sesión expirada", 1);
        navigate('/logout');
      } else {
        addToast("Error en el servidor, intente nuevamente", 2);
      }
      setFetchingWidgetInfo(false);
    }
  };

  const refreshParameters = async (selectedProcess, fieldName) => {
    try {
      if (fieldName === "conformValue") setFetchingConformParams(true);
      if (fieldName === "noConformValue") setFetchingNoConformParams(true);
      if (fieldName === "reValue") setFetchingReParams(true);

      const response = await getAllParameters(selectedProcess, auth?.token);
      const mapped = response.map(param => ({
        key: param.key,
        alias: param.alias,
        type: param.datatype
      }));

      if (fieldName === "conformValue") {
        setConformParameters(mapped);
        setFetchingConformParams(false);
      }
      if (fieldName === "noConformValue") {
        setNoConformParameters(mapped);
        setFetchingNoConformParams(false);
      }
      if (fieldName === "reValue") {
        setReParameters(mapped);
        setFetchingReParams(false);
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 419) {
        addToast("Sesión expirada", 1);
        navigate('/logout');
      } else {
        addToast("Error cargando parámetros del proceso", 2);
      }
      if (fieldName === "conformValue") setFetchingConformParams(false);
      if (fieldName === "noConformValue") setFetchingNoConformParams(false);
      if (fieldName === "reValue") setFetchingReParams(false);
    }
  };

  const handleProcessChange = (selectedProcess, fieldName) => {
    setConf(prev => {
      if (!prev || !prev.quality) return prev;
      const existingField = prev.quality[fieldName];
      if (existingField && existingField.process && existingField.process.id === selectedProcess) {
        return prev;
      }
      return {
        ...prev,
        quality: {
          ...prev.quality,
          [fieldName]: undefined
        }
      };
    });
  };

  useEffect(() => {
    if (selectedConformProcess) {
      refreshParameters(selectedConformProcess, "conformValue");
      handleProcessChange(selectedConformProcess, "conformValue");
    }
  }, [selectedConformProcess]);

  useEffect(() => {
    if (selectedNoConformProcess) {
      refreshParameters(selectedNoConformProcess, "noConformValue");
      handleProcessChange(selectedNoConformProcess, "noConformValue");
    }
  }, [selectedNoConformProcess]);

  useEffect(() => {
    if (selectedReProcess) {
      refreshParameters(selectedReProcess, "reValue");
      handleProcessChange(selectedReProcess, "reValue");
    }
  }, [selectedReProcess]);

  useEffect(() => {
    const bdConf = {
      groupList: undefined,
      quality: {
        conformValue: undefined,
        noConformValue: undefined,
        reValue: undefined,
      },
      dateRange: {
        activeTab: "customDates",
        selectedRange: "byDashboard"
      },
      nDecimals: 2,
      isRate: false,
      dateFormat: "mm/dd/yy - hh:mm:ss",
      graphType: "line",
      colorPalette: "fixed",
      fixedColor: "#4287f5",
      textColor: "#000000"
    };

    setLoading(true);
    let processOptions = {};
    menuItems.slice(1).forEach(element => {
      processOptions[element.id] = element.label;
    });

    if (id) {
      refreshWidgetInfo();
    } else {
      setConf(bdConf);
      setFetchingWidgetInfo(false);
    }
    setProcessList(processOptions);
    setLoading(false);
  }, []);

  useEffect(() => {
    const basicFieldsValid =
      title !== "" &&
      conf?.quality?.conformValue?.target?.key &&
      conf?.quality?.noConformValue?.target?.key &&
      conf?.dateRange &&
      conf?.nDecimals !== undefined &&
      conf?.nDecimals !== "";

    if (basicFieldsValid) {
      setValidate(true);
    } else {
      setValidate(false);
    }
  }, [conf, title]);

  const createWidgetDB = async () => {
    try {
      setCreatingWidget(true);
      const position = assignSpace();
      const response = await createWidget({
        type: "processQuality",
        title: title,
        description: "",
        configuration: conf,
        dashboardId: dashboard._id,
        x: position.x,
        y: position.y,
        width: 200,
        height: 200,
        selectedTab
      }, auth?.token);

      addCard(
        response._id,
        response.title,
        response.type,
        response.dashboardId
      );
      onSubmit();
      setCreatingWidget(false);
      addToast("Widget " + title + " creado exitosamente.", 1);
    } catch (error) {
      console.error(error);
      setCreatingWidget(false);
      if (error?.response?.status === 404) {
        addToast("Proceso no encontrado", 3);
      } else if (error?.response?.status === 419) {
        addToast("Sesión expirada", 1);
        navigate('/logout');
      } else {
        addToast("Error en el servidor, intente nuevamente", 2);
      }
    }
  };

  const editWidgetDB = async () => {
    try {
      setCreatingWidget(true);
      await updateWidget(id, {
        type: "processQuality",
        title: title,
        description: "",
        configuration: conf,
      }, auth?.token);

      onSubmit();
      setCreatingWidget(false);
      addToast("Widget " + title + " editado exitosamente.", 1);
    } catch (error) {
      console.error(error);
      setCreatingWidget(false);
      if (error?.response?.status === 404) {
        addToast("Proceso no encontrado", 3);
      } else if (error?.response?.status === 419) {
        addToast("Sesión expirada", 1);
        navigate('/logout');
      } else {
        addToast("Error en el servidor, intente nuevamente", 2);
      }
    }
  };

  const handleDataRangeChange = (selectedRange) => {
    setDataRange(translateDatePicker(selectedRange));
    setConf(prev => ({ ...prev, dateRange: selectedRange }));
  };

  const handleDecimalsChange = (decimals) => {
    setConf(prev => ({ ...prev, nDecimals: decimals }));
  };

  const handleGraphType = (graphType) => {
    setConf(prev => ({ ...prev, graphType: graphType }));
  };

  const selectColorPalette = (selectedColor) => {
    if (selectedColor) {
      setConf(prev => ({ ...prev, colorPalette: selectedColor }));
    }
  };

  const selectFixedColor = (selectedColor) => {
    if (selectedColor) {
      setConf(prev => ({ ...prev, fixedColor: selectedColor }));
    }
  };

  const selectTextColor = (selectedColor) => {
    if (selectedColor) {
      setConf(prev => ({ ...prev, textColor: selectedColor }));
    }
  };

  const findParam = (list, selected) =>
    list.find(p => p?.alias === selected || p?.key === selected);

  const setTargetField = (valueName, selectedField, paramList, selectedProcess) => {
    if (!selectedField) return;
    const param = findParam(paramList, selectedField);
    if (!param) return;

    setConf(prev => {
      const currentField = prev?.quality?.[valueName] || {};
      return {
        ...prev,
        quality: {
          ...prev.quality,
          [valueName]: {
            ...currentField,
            process: {
              id: selectedProcess,
              name: processList[selectedProcess]
            },
            target: {
              key: param.key,
              alias: param.alias || param.key,
              type: param.type
            },
            where: currentField.where || []
          }
        }
      };
    });
  };

  const setTimeVarField = (valueName, selectedField, paramList) => {
    if (!selectedField) return;
    const param = findParam(paramList, selectedField);
    if (!param) return;

    setConf(prev => {
      const currentField = prev?.quality?.[valueName] || {};
      return {
        ...prev,
        quality: {
          ...prev.quality,
          [valueName]: {
            ...currentField,
            timeVar: {
              key: param.key,
              alias: param.alias || param.key,
              type: "date"
            }
          }
        }
      };
    });
  };

  const addFilter = (valueName, selectedProcess) => {
    setConf(prev => {
      const currentField = prev?.quality?.[valueName] || {
        process: {
          id: selectedProcess,
          name: processList[selectedProcess]
        },
        where: []
      };
      const currentWhere = currentField.where || [];
      return {
        ...prev,
        quality: {
          ...prev.quality,
          [valueName]: {
            ...currentField,
            process: {
              id: selectedProcess,
              name: processList[selectedProcess]
            },
            where: [
              ...currentWhere,
              { field: "", operator: "equal", value: "" }
            ]
          }
        }
      };
    });
  };

  const updateFilter = (valueName, index, keyToUpdate, val) => {
    setConf(prev => {
      const currentField = prev?.quality?.[valueName];
      if (!currentField || !currentField.where) return prev;
      const newWhere = [...currentField.where];
      newWhere[index] = {
        ...newWhere[index],
        [keyToUpdate]: val
      };
      return {
        ...prev,
        quality: {
          ...prev.quality,
          [valueName]: {
            ...currentField,
            where: newWhere
          }
        }
      };
    });
  };

  const removeFilter = (valueName, index) => {
    setConf(prev => {
      const currentField = prev?.quality?.[valueName];
      if (!currentField || !currentField.where) return prev;
      const newWhere = currentField.where.filter((_, i) => i !== index);
      return {
        ...prev,
        quality: {
          ...prev.quality,
          [valueName]: {
            ...currentField,
            where: newWhere
          }
        }
      };
    });
  };

  const deleteValue = (valueName) => {
    setConf(prev => {
      if (!prev || !prev.quality) return prev;
      const newQuality = { ...prev.quality };
      delete newQuality[valueName];
      return {
        ...prev,
        quality: newQuality
      };
    });
  };

  const deleteConformValue = () => deleteValue("conformValue");
  const deleteNoConformValue = () => deleteValue("noConformValue");
  const deleteReValue = () => deleteValue("reValue");

  const renderStage = (valueName, selectedProcess, parameters, headerText) => {
    const currentField = conf?.quality?.[valueName];
    const filters = currentField?.where || [];

    return (
      <div className="flex flex-col gap-4 h-[28rem] relative overflow-auto p-2">
        <div className="flex flex-col flex-grow gap-4">
          <span className="font-semibold text-lg">
            {processList[selectedProcess]} - {headerText}
          </span>

          <div className="w-full flex gap-2 items-center">
            <div className="basis-1/4 font-medium">Valor objetivo:</div>
            <div className="basis-3/4">
              <DropdownSelectableList
                items={parameters
                  ?.filter(p => p.type === "numeric" || p.type === "time")
                  ?.map(p => p.alias || p.key)}
                multiSelect={false}
                selection={currentField?.target?.alias || currentField?.target?.key}
                setItems={(selected) => {
                  if (selected) setTargetField(valueName, selected, parameters, selectedProcess);
                }}
              />
            </div>
          </div>

          <div className="w-full flex gap-2 items-center">
            <div className="basis-1/4 font-medium">
              Variable de tiempo <span className="text-gray-400 font-normal text-xs">(Opcional)</span>:
            </div>
            <div className="basis-3/4">
              <DropdownSelectableList
                items={parameters
                  ?.filter(p => p.type === "date")
                  ?.map(p => p.alias || p.key)}
                multiSelect={false}
                selection={currentField?.timeVar?.alias || currentField?.timeVar?.key}
                setItems={(selected) => {
                  if (selected) setTimeVarField(valueName, selected, parameters);
                }}
              />
            </div>
          </div>

          <div className="border-t pt-3 mt-2">
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium text-gray-700">Filtros (Donde):</span>
              <ButtonForm
                text="+ Agregar filtro"
                enabled={true}
                action={() => addFilter(valueName, selectedProcess)}
              />
            </div>

            {filters.length === 0 && (
              <div className="text-sm text-gray-400 italic py-2">
                No hay filtros agregados.
              </div>
            )}

            <div className="flex flex-col gap-2">
              {filters.map((filter, idx) => (
                <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded border">
                  <div className="basis-1/3">
                    <DropdownSelectableList
                      items={parameters?.map(p => p.alias || p.key)}
                      multiSelect={false}
                      selection={findParam(parameters, filter.field)?.alias || filter.field}
                      setItems={(selected) => {
                        if (!selected) return;
                        const param = findParam(parameters, selected);
                        if (param) updateFilter(valueName, idx, "field", param.key);
                      }}
                    />
                  </div>
                  <div className="basis-1/4">
                    <select
                      className="border px-2 py-1 rounded w-full text-sm bg-white"
                      value={filter.operator || "equal"}
                      onChange={(e) => updateFilter(valueName, idx, "operator", e.target.value)}
                    >
                      {OPERATOR_OPTIONS.map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="basis-1/3">
                    <input
                      type="text"
                      placeholder="Valor"
                      className="border px-2 py-1 rounded w-full text-sm bg-white"
                      value={filter.value || ""}
                      onChange={(e) => updateFilter(valueName, idx, "value", e.target.value)}
                    />
                  </div>
                  <div className="basis-1/12 flex justify-end">
                    <button
                      type="button"
                      className="p-1 hover:bg-red-50 rounded-full text-red-500 font-bold"
                      onClick={() => removeFilter(valueName, idx)}
                      title="Eliminar filtro"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-none sticky bottom-0 bg-white p-2 shadow-lg border-t mt-2">
          <div className="flex w-full justify-end">
            <ButtonForm text="Guardar" enabled={true} action={() => setStage("form")} />
          </div>
        </div>
      </div>
    );
  };

  const getCommonGroupParameters = () => {
    const activeLists = [];
    if (selectedConformProcess && conformParameters?.length > 0) activeLists.push(conformParameters);
    if (selectedNoConformProcess && noConformParameters?.length > 0) activeLists.push(noConformParameters);
    if (selectedReProcess && reParameters?.length > 0) activeLists.push(reParameters);

    if (activeLists.length === 0) return [];

    return activeLists[0]
      .filter(p => p.type !== "date" && p.type !== "time")
      .filter(param => {
        return activeLists.every(list => list.some(otherParam => otherParam.key === param.key));
      });
  };

  const selectGroupList = (selected, parameters) => {
    if (!selected) return;
    const param = findParam(parameters, selected);
    if (!param) return;

    setConf(prev => ({
      ...prev,
      groupList: {
        key: param.key,
        alias: param.alias || param.key,
        type: param.type
      }
    }));
  };

  const deleteGroupList = () => {
    setConf(prev => ({
      ...prev,
      groupList: undefined
    }));
  };

  const renderGroupListStage = (parameters, headerText) => (
    <div className="flex flex-col gap-4 h-[28rem] relative overflow-auto p-2">
      <div className="flex flex-col flex-grow gap-4">
        <span className="font-semibold text-lg">
          {headerText}
        </span>

        <div className="w-full flex gap-2 items-center">
          <div className="basis-1/4 font-medium">Agrupar por:</div>
          <div className="basis-3/4">
            <DropdownSelectableList
              items={parameters?.map(p => p.alias || p.key)}
              multiSelect={false}
              selection={conf?.groupList?.alias || conf?.groupList?.key}
              setItems={(selected) => {
                if (selected) selectGroupList(selected, parameters);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex-none sticky bottom-0 bg-white p-2 shadow-lg border-t mt-2">
        <div className="flex w-full justify-end">
          <ButtonForm text="Guardar" enabled={true} action={() => setStage("form")} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {stage === "form" && (
        <>
          {conf && !fetchingWidgetInfo && (
            <div className='flex flex-col gap-2 h-[35rem] relative overflow-auto'>
              <div className='flex flex-col flex-grow gap-2'>
                <CollapsableSection title={"Datos"} titleBgColor='bg-lightblue' titleTextColor='text-bluesens' open={true} border={false}>
                  <div className='flex flex-col w-full gap-2'>
                    <CollapsableSection
                      CustomTitleBar={() => <span>Calidad</span>}
                      titleBgColor='bg-aliceblue'
                      titleTextColor='text-gray'
                      open={true}
                    >
                      <div className='w-full flex flex-col px-4 gap-2'>
                        {/* Valor conforme */}
                        <CollapsableSection
                          title={"Valor conforme (datos numéricos)"}
                          titleBgColor='bg-aliceblue'
                          titleTextColor='text-gray'
                          open={true}
                        >
                          <div className='w-full flex flex-col px-4 gap-2'>
                            <div className='w-[60%] mb-2'>
                              <SelectionForm
                                text="Proceso" 
                                options={Object.keys(processList)} 
                                mask={processList} 
                                value={selectedConformProcess}
                                onChange={event => setSelectedConformProcess(event)}
                                AppendClassName='-mb-5'
                                error={!!selectedConformProcess && !fetchingConformParams && conformParameters?.length === 0 ? 'El proceso no tiene parámetros creados' : ""}
                              /> 
                            </div>
                            { conf?.quality?.conformValue && (
                              <ColumnField header={conf.quality.conformValue} onDelete={deleteConformValue}/>
                            )}
                            { !conf?.quality?.conformValue && (
                              <div className='mx-auto'>
                                <ButtonForm 
                                  text={"Agregar campo"} 
                                  Icon={() => <AddIco fill='white'/>} 
                                  enabled={!!selectedConformProcess && !fetchingConformParams && conformParameters?.length !== 0}
                                  action={() => setStage("setConformValue")}
                                />
                              </div>
                            )}
                          </div>
                        </CollapsableSection>
                        
                        {/* Valor NO conforme */}
                        <CollapsableSection
                          title={"Valor NO conforme (datos numéricos)"}
                          titleBgColor='bg-aliceblue'
                          titleTextColor='text-gray'
                          open={true}
                        >
                          <div className='w-full flex flex-col px-4 gap-2'>
                            <div className='w-[60%] mb-2'>
                              <SelectionForm
                                text="Proceso" 
                                options={Object.keys(processList)} 
                                mask={processList} 
                                value={selectedNoConformProcess}
                                onChange={event => setSelectedNoConformProcess(event)}
                                AppendClassName='-mb-5'
                                error={!!selectedNoConformProcess && !fetchingNoConformParams && noConformParameters?.length === 0 ? 'El proceso no tiene parámetros creados' : ""}
                              /> 
                            </div>
                            { conf?.quality?.noConformValue && (
                              <ColumnField header={conf.quality.noConformValue} onDelete={deleteNoConformValue}/>
                            )}
                            { !conf?.quality?.noConformValue && (
                              <div className='mx-auto'>
                                <ButtonForm 
                                  text={"Agregar campo"} 
                                  Icon={() => <AddIco fill='white'/>} 
                                  enabled={!!selectedNoConformProcess && !fetchingNoConformParams && noConformParameters?.length !== 0}
                                  action={() => setStage("setNoConformValue")}
                                />
                              </div>
                            )}
                          </div>
                        </CollapsableSection>

                        {/* Merma */}
                        <CollapsableSection
                          title={"Merma (datos numéricos)"}
                          titleBgColor='bg-aliceblue'
                          titleTextColor='text-gray'
                          open={true}
                        >
                          <div className='w-full flex flex-col px-4 gap-2'>
                            <div className='w-[60%] mb-2'>
                              <SelectionForm
                                text="Proceso" 
                                options={Object.keys(processList)} 
                                mask={processList} 
                                value={selectedReProcess}
                                onChange={event => setSelectedReProcess(event)}
                                AppendClassName='-mb-5'
                                error={!!selectedReProcess && !fetchingReParams && reParameters?.length === 0 ? 'El proceso no tiene parámetros creados' : ""}
                              /> 
                            </div>
                            { conf?.quality?.reValue && (
                              <ColumnField header={conf.quality.reValue} onDelete={deleteReValue}/>
                            )}
                            { !conf?.quality?.reValue && (
                              <div className='mx-auto'>
                                <ButtonForm 
                                  text={"Agregar campo"} 
                                  Icon={() => <AddIco fill='white'/>} 
                                  enabled={!!selectedReProcess && !fetchingReParams && reParameters?.length !== 0}
                                  action={() => setStage("setReValue")}
                                />
                              </div>
                            )}
                          </div>
                        </CollapsableSection>
                      </div>
                    </CollapsableSection>

                    {/* Agrupar por */}
                    <CollapsableSection
                      title={"Agrupar por"}
                      titleBgColor='bg-aliceblue'
                      titleTextColor='text-gray'
                      open={true}
                    >
                      <div className='w-full flex flex-col px-4 gap-2'>
                        { conf?.groupList && (
                          <ColumnField header={conf.groupList} onDelete={deleteGroupList}/>
                        )}
                        { !conf?.groupList && (
                          <div className='mx-auto flex flex-col items-center gap-1'>
                            <ButtonForm 
                              text={"Agregar campo"} 
                              Icon={() => <AddIco fill='white'/>} 
                              enabled={getCommonGroupParameters().length > 0}
                              action={() => setStage("setGroupList")}
                            />
                            {!(selectedConformProcess || selectedNoConformProcess || selectedReProcess) ? (
                              <span className="text-xs text-gray-500 italic text-center">
                                Seleccione al menos un proceso arriba para ver parámetros comunes.
                              </span>
                            ) : getCommonGroupParameters().length === 0 ? (
                              <span className="text-xs text-red-500 italic text-center">
                                No hay parámetros comunes entre los procesos seleccionados.
                              </span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </CollapsableSection>

                    <div className='-mb-5 pt-4'>
                      <InputForm 
                        type={"datarange"} 
                        text={"Rango de tiempo"} 
                        value={dataRange} 
                        AppendClassName='text-left' 
                        onChange={(event) => handleDataRangeChange(event)}
                        dateRangeOptions={conf.dateRange}
                        addedRanges={{ "byDashboard": () => {} }} 
                      />
                    </div>
                  </div>
                </CollapsableSection>

                <CollapsableSection title={"Apariencia"} titleBgColor='bg-lightblue' titleTextColor='text-bluesens' border={false} open={true}>
                  <div className='-mb-4 '>
                    <InputForm text={"Nombre"} value={title} onChange={(event) => setTitle(event.target.value)}/>
                  </div>
                  <div className='-mb-4 pt-2'>
                    <InputForm type={"number"} text={"Decimales"} value={conf.nDecimals} onChange={(event) => handleDecimalsChange(event.target.value)}/>
                  </div>
                  {!conf.hasInterval && (
                    <div className='w-full -mb-4 pt-2'>
                      <SelectionForm
                        text="Tipo de gráfica" 
                        options={Object.keys(graphTypes)} 
                        mask={graphTypes} 
                        value={conf.graphType}
                        onChange={event => handleGraphType(event)}
                        AppendClassName=' '
                      /> 
                    </div>
                  )}
                  <div className='-mb-4 pt-2'>
                    <InputForm type={"button"} text={"Estilo"} value={"Modificar estilo"} AppendClassName='text-left' onClick={() => setStage("setAparience")}/>
                  </div>
                </CollapsableSection>
              </div>

              <div className='flex-none sticky bottom-0 bg-white p-2 shadow-lg'>
                <div className='flex w-full justify-end'>
                  <button type='button' onClick={() => id ? editWidgetDB() : createWidgetDB()} disabled={!validate || creatingWidget}>
                    <CheckIco width='50px' height='50px' fill={validate ? 'bluesens': 'gray'}/>
                  </button>
                </div>
              </div>
            </div>
          )}
          {(!conf || fetchingWidgetInfo || loading) && (
            <Spinner />
          )}
        </>
      )}

      {stage === "setConformValue" &&
        renderStage("conformValue", selectedConformProcess, conformParameters, "Valor conforme")}

      {stage === "setNoConformValue" &&
        renderStage("noConformValue", selectedNoConformProcess, noConformParameters, "Valor NO conforme")}

      {stage === "setReValue" &&
        renderStage("reValue", selectedReProcess, reParameters, "Merma")}

      {stage === "setGroupList" &&
        renderGroupListStage(getCommonGroupParameters(), "Agrupar por")}

      {stage === "setAparience" && (
        <div className="flex flex-col gap-2 h-[25rem] relative overflow-auto p-2">
          <div className="flex flex-col flex-grow gap-2">
            <span className="font-semibold text-lg">Modificar estilo</span>
            <div className="pt-2">
              <WidgetHeaderStyleFields
                headerColor={conf?.headerColor}
                headerTextColor={conf?.headerTextColor}
                onChange={(field, val) => setConf(prev => ({ ...prev, [field]: val }))}
              />
            </div>

            <span className='text-sm font-medium text-gray-500 mt-1'>Gráfico</span>
            <SelectionForm
              id="colorPalette"
              text="Paleta de colores"
              options={Object.keys(colorOptions)}
              mask={colorOptions}
              value={conf?.colorPalette || "pastel"}
              onChange={(event) => selectColorPalette(event)}
            compact
            />

            {conf?.colorPalette === "fixed" && (
              <InputForm
                id="fixedColor"
                type="color"
                text="Seleccionar color fijo"
                value={conf?.fixedColor || "#4287f5"}
                onChange={(event) => selectFixedColor(event)}
              compact
              />
            )}
            <InputForm
              id="textColor"
              type="color"
              text="Seleccionar color del texto"
              value={conf?.textColor || "#000000"}
              onChange={(event) => selectTextColor(event)}
            compact
            />
          </div>

          <div className="flex-none sticky bottom-0 bg-white p-2 shadow-lg border-t mt-2">
            <div className="flex w-full justify-end">
              <ButtonForm
                text={"Guardar"}
                enabled={true}
                action={() => setStage("form")}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QualityForm;
